export type ItemType = "check" | "reading" | "note";
export type Evidence = "none" | "photo" | "video" | "optional" | "note";

export interface WalkaroundItem {
  id: string;
  label: string;
  type: ItemType;
  evidence: Evidence;
  required: boolean;
  hint: string;
}

export interface WalkaroundSection {
  id: string;
  title: string;
  items: WalkaroundItem[];
}

export interface WalkaroundTemplate {
  id: string;
  name: string;
  frequency: "hourly" | "daily" | "weekly";
  description: string;
  sections: WalkaroundSection[];
}

const it = (id: string, label: string, type: ItemType = "check", evidence: Evidence = "optional", hint = ""): WalkaroundItem => ({ id, label, type, evidence, required: true, hint });

const hourly: WalkaroundTemplate = {
  id: "wr-hourly",
  name: "Hourly Walkaround",
  frequency: "hourly",
  description: "Every hour safety, security and resident wellbeing round. Capture photo or video evidence for anything failed or unusual.",
  sections: [
    { id: "h-sec", title: "Premises Security", items: [
      it("h-sec-1", "Main entrance door closed and locked, no tailgating risk", "check", "photo"),
      it("h-sec-2", "All fire exits clear, unobstructed and closed", "check", "photo"),
      it("h-sec-3", "Visitors signed in and accompanied where required", "check"),
      it("h-sec-4", "No unknown persons or suspicious activity on premises", "check"),
    ]},
    { id: "h-safe", title: "Safety Checks", items: [
      it("h-safe-1", "No smell of smoke, gas or burning; no visible hazards", "check", "photo"),
      it("h-safe-2", "Corridors, stairs and floors clear of trip hazards", "check", "photo"),
      it("h-safe-3", "Lighting adequate in all occupied and circulation areas", "check"),
      it("h-safe-4", "Room and corridor temperatures comfortable", "reading", "optional", "Record degrees C"),
    ]},
    { id: "h-res", title: "Resident Wellbeing Round", items: [
      it("h-res-1", "Every resident seen, comfortable and safe", "check", "video"),
      it("h-res-2", "Call bells and intercoms within reach and working", "check", "photo"),
      it("h-res-3", "Fluids offered, intake encouraged and recorded", "check"),
      it("h-res-4", "Toileting needs checked and recorded", "check"),
      it("h-res-5", "Pressure care repositioning done as per care plan", "check", "note" as any, "Add note for any resident repositioned"),
    ]},
    { id: "h-env", title: "Environment", items: [
      it("h-env-1", "Lounges and dining room tidy, clean and welcoming", "check", "photo"),
      it("h-env-2", "Toilets clean, stocked with soap, towels and paper", "check", "photo"),
      it("h-env-3", "Hand hygiene stations stocked at all points", "check"),
      it("h-env-4", "Signage intact and readable", "check"),
    ]},
  ],
};

const daily: WalkaroundTemplate = {
  id: "wr-daily",
  name: "Daily Walkaround",
  frequency: "daily",
  description: "Full daily management walkaround covering opening checks, kitchen, care records, infection prevention and evening security.",
  sections: [
    { id: "d-open", title: "Morning Opening Checks", items: [
      it("d-open-1", "Staff signed in; rota covers all shifts safely", "check"),
      it("d-open-2", "Keys and fobs counted and verified", "note", "none", "Record count and who verified"),
      it("d-open-3", "Fridge temperature 0 to 5 C recorded", "reading", "photo"),
      it("d-open-4", "Freezer temperature minus 18 C or below recorded", "reading", "photo"),
      it("d-open-5", "Medication cabinet and medicines fridge temperatures recorded", "reading", "photo"),
      it("d-open-6", "Hot water at sentinel tap 50 C or above", "reading", "photo"),
      it("d-open-7", "Heating, ventilation and windows working and secure", "check"),
    ]},
    { id: "d-kit", title: "Kitchen and Dining", items: [
      it("d-kit-1", "Food covered, labelled and in date", "check", "photo"),
      it("d-kit-2", "Food hygiene charts completed and up to date", "check"),
      it("d-kit-3", "Dishwasher clean and functioning; probe thermometer available", "check", "photo"),
      it("d-kit-4", "Dining room set for meals; tables and chairs clean", "check", "photo"),
      it("d-kit-5", "Special diet requirements checked against meals served", "check", "note"),
    ]},
    { id: "d-care", title: "Care and Records", items: [
      it("d-care-1", "MAR charts checked for missed or unsigned entries", "check", "note"),
      it("d-care-2", "Care plans reflect current needs and recent changes", "check"),
      it("d-care-3", "Night incidents reviewed and actions completed", "check", "note"),
      it("d-care-4", "Each resident spoken with; concerns recorded", "check", "video"),
    ]},
    { id: "d-ipc", title: "Infection Prevention and Control", items: [
      it("d-ipc-1", "Hand hygiene stations stocked throughout the home", "check", "photo"),
      it("d-ipc-2", "PPE available in correct sizes at point of use", "check"),
      it("d-ipc-3", "Sluice clean, stocked and used correctly", "check", "photo"),
      it("d-ipc-4", "Laundry handled and segregated correctly", "check"),
      it("d-ipc-5", "Waste segregated into correct streams", "check", "photo"),
    ]},
    { id: "d-eve", title: "Evening Security Checks", items: [
      it("d-eve-1", "All external doors locked and alarms set where applicable", "check", "photo"),
      it("d-eve-2", "Windows closed and locked in unoccupied rooms", "check"),
      it("d-eve-3", "Fire doors closed and not propped open", "check", "photo"),
      it("d-eve-4", "External lighting and timers working", "check"),
    ]},
  ],
};
const weekly: WalkaroundTemplate = {
  id: "wr-weekly",
  name: "Weekly Walkaround",
  frequency: "weekly",
  description: "Comprehensive weekly audit covering fire safety, water safety, equipment, health and safety, infection control and medications.",
  sections: [
    { id: "w-fire", title: "Fire Safety", items: [
      it("w-fire-1", "Fire alarm test completed and recorded with zone identified", "check", "note"),
      it("w-fire-2", "Fire escapes and routes checked and kept clear", "check", "photo"),
      it("w-fire-3", "Fire doors, seals and closers in good order", "check", "photo"),
      it("w-fire-4", "Extinguishers in position, gauges in green and tags in date", "check", "photo"),
      it("w-fire-5", "Fire signage and exit signs intact and illuminated", "check", "photo"),
    ]},
    { id: "w-water", title: "Water Safety and Legionella", items: [
      it("w-water-1", "Sentinel tap temperatures recorded: hot 50 C or above, cold 20 C or below", "reading", "photo"),
      it("w-water-2", "Shower and bath outlet temperatures recorded", "reading", "photo"),
      it("w-water-3", "Outlets clean, free of scale and not dripping", "check", "photo"),
      it("w-water-4", "Little and often flushing completed for infrequently used outlets", "check", "note"),
    ]},
    { id: "w-equip", title: "Equipment and Moving Handling", items: [
      it("w-equip-1", "Hoists visually checked, LOLER tag in date and charge working", "check", "photo"),
      it("w-equip-2", "Slings checked for wear, fraying and label readability", "check", "photo"),
      it("w-equip-3", "Wheelchairs: brakes, tyres and footplates checked", "check", "photo"),
      it("w-equip-4", "Profiling beds and mattresses checked for damage and pressure rating", "check", "photo"),
      it("w-equip-5", "Call bell and intercom tested in every room", "check", "note"),
    ]},
    { id: "w-hs", title: "Health and Safety", items: [
      it("w-hs-1", "First aid kits stocked, in date and signed", "check", "photo"),
      it("w-hs-2", "COSHH sheets available; chemicals stored locked and labelled", "check", "photo"),
      it("w-hs-3", "Steps, ladders and heavy items stored safely", "check"),
      it("w-hs-4", "Garden, paths and outdoor seating safe and free of hazards", "check", "photo"),
      it("w-hs-5", "Vehicle checks completed where vehicles are used", "check", "note"),
    ]},
    { id: "w-ipc", title: "Infection Control Deep Check", items: [
      it("w-ipc-1", "Fridges and freezers deep cleaned; temperature logs reviewed", "check", "photo"),
      it("w-ipc-2", "Clinical waste store clean, locked and within capacity", "check", "photo"),
      it("w-ipc-3", "Hand hygiene audit completed with scores recorded", "check", "note"),
      it("w-ipc-4", "Cleaning schedules signed and complete for the week", "check", "photo"),
    ]},
    { id: "w-med", title: "Medications", items: [
      it("w-med-1", "Medication stock checked against requirements", "check", "note"),
      it("w-med-2", "Controlled drugs book balance checked and signed by two staff", "check", "note"),
      it("w-med-3", "Sample MAR audit completed with errors actioned", "check", "note"),
      it("w-med-4", "Medicines fridge temperature log reviewed for excursions", "reading", "photo"),
    ]},
  ],
};

export const walkaroundTemplates: WalkaroundTemplate[] = [hourly, daily, weekly];